import { useMapViewState } from '@components/map/useMapViewState';
import { useMapMeasureState } from '@hooks/useMapMeasureState';
import { useMapSelectedPositionState } from '@hooks/useMapSelectedPositionState';
import { DEFAULT_THRESHOLD } from '@utils/map/map.constants';
import { Position } from 'geojson';
import { Map } from 'mapbox-gl';
import { createContext, PropsWithChildren, RefObject, useContext, useMemo, useState } from 'react';
import { ViewState } from 'react-map-gl/mapbox';

import { Coordinates, NullableCoordinates } from '@/types/commons/commons.types';
import {
  FilterMode,
  LayerNameEnum,
  MapIdEnum,
  OperationalMapId,
  StatisticFilterColorMode,
  ViewMode,
} from '@/types/map.types';

export interface SidebarDynamicOptions {
  showZones: boolean;
  showInfo: boolean;
  showDailyFlightPlans: boolean;
  showNeutralInfo: boolean;
  showFilters: FilterMode;
  showOngoingDroneNotifications: boolean;
}

const DEFAULT_SIDEBAR_OPTIONS: SidebarDynamicOptions = {
  showZones: true,
  showInfo: true,
  showDailyFlightPlans: true,
  showNeutralInfo: true,
  showFilters: 'regular',
  showOngoingDroneNotifications: true,
};

const sidebarConfig: Record<OperationalMapId, SidebarDynamicOptions> = {
  [MapIdEnum.LIVE]: DEFAULT_SIDEBAR_OPTIONS,
  [MapIdEnum.REPLAY]: {
    ...DEFAULT_SIDEBAR_OPTIONS,
    showZones: false,
    showInfo: false,
    showOngoingDroneNotifications: false,
  },
  [MapIdEnum.LAD]: DEFAULT_SIDEBAR_OPTIONS,
  [MapIdEnum.STATISTICS]: {
    showZones: false,
    showInfo: false,
    showDailyFlightPlans: false,
    showNeutralInfo: false,
    showFilters: 'statistics',
    showOngoingDroneNotifications: false,
  },
};

type MapContextProps = {
  mapConstraintsRef: RefObject<HTMLDivElement | null>;
  mapId: OperationalMapId;
  infoLayers: LayerNameEnum[];
  center: Coordinates | null;
};

type BaseMapContextType = MapContextProps & {
  map: Map | null;
  setMap: (map: Map | null) => void;
  isMeasureActive: boolean;
  setIsMeasureActive: (isMeasureActive: boolean) => void;
  sidebarOptions: SidebarDynamicOptions;
  mousePosition: Coordinates | NullableCoordinates | null;
  setMousePosition: (coordinates: NullableCoordinates | null) => void;
  isClickEnabled: boolean;
  setIsClickEnabled: (isClickEnabled: boolean) => void;
  measurePoints: Position[];
  setMeasurePoints: (points: Position[]) => void;
  mouseSelectedPosition: NullableCoordinates;
  isSelectionEnabled: boolean;
  setSelectionOn: (active: boolean) => void;
  setSelectedPosition: (position: Coordinates) => void;

  viewMode: ViewMode;
  setViewMode: (viewMode: ViewMode) => void;
  isNorthLocked: boolean;
  setIsNorthLocked: (isNorthLocked: boolean) => void;
  viewState: ViewState;
  setViewState: (viewState: ViewState) => void;
  setZoom: (zoom: number) => void;
};

type StatisticsContextType = Omit<BaseMapContextType, 'mapId'> & {
  mapId: MapIdEnum.STATISTICS;
  filterColorMode: StatisticFilterColorMode;
  setFilterColorMode: (colorMode: StatisticFilterColorMode) => void;
  threshold: number;
  setThreshold: (threshold: number) => void;
};

type MapContextType = BaseMapContextType | StatisticsContextType;

const MapContext = createContext<MapContextType | null>(null);

export const MapContextProvider = ({
  mapConstraintsRef,
  center,
  mapId,
  infoLayers,
  children,
}: PropsWithChildren<MapContextProps>) => {
  const { state: mapViewState, setViewState, setZoom, setViewMode, setIsNorthLocked } = useMapViewState(center);
  const { state, setIsMeasureActive, setIsClickEnabled, setMousePosition, setMeasurePoints } = useMapMeasureState();
  const { positionState, setSelectionOn, setSelectedPosition } = useMapSelectedPositionState();
  const sidebarOptions = sidebarConfig[mapId];

  const [filterColorMode, setFilterColorMode] = useState<StatisticFilterColorMode>('byMaxHeight');
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  const [map, setMap] = useState<Map | null>(null);

  const values = useMemo(() => {
    const base = {
      map,
      mapConstraintsRef,
      mapId,
      infoLayers,
      sidebarOptions,
      ...mapViewState,
      ...positionState,
      center,
      setMap,
      setSelectionOn,
      setSelectedPosition,
      ...state,
      setIsMeasureActive,
      setIsClickEnabled,
      setMeasurePoints,
      setMousePosition,
      setViewMode,
      setIsNorthLocked,
      setViewState,
      setZoom,
    };

    if (mapId === MapIdEnum.STATISTICS) {
      return {
        ...base,
        filterColorMode,
        setFilterColorMode,
        threshold,
        setThreshold,
      };
    }

    return base;
  }, [
    map,
    mapConstraintsRef,
    mapId,
    infoLayers,
    sidebarOptions,
    mapViewState,
    positionState,
    center,
    setSelectionOn,
    setSelectedPosition,
    state,
    setIsMeasureActive,
    setIsClickEnabled,
    setMeasurePoints,
    setMousePosition,
    setViewMode,
    setIsNorthLocked,
    setViewState,
    setZoom,
    filterColorMode,
    threshold,
  ]);

  return <MapContext value={values}>{children}</MapContext>;
};

export const useMapContext = (): BaseMapContextType => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within a MapContextProvider');
  }
  return context;
};

export const useStatisticMapContext = (): StatisticsContextType => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMapContext must be used within a MapContextProvider');
  }

  if (context.mapId !== MapIdEnum.STATISTICS) {
    throw new Error('useStatisticMapContext can only be used with a Statistics map');
  }

  return context as StatisticsContextType;
};
